import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import pickleCourtsLogo from '../assets/picklecourts-logo.png';

export default function LoginScreen() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(searchParams.get('forgot') === 'true');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const result = await login(email, password);
      // SUPERADMIN without a venue should go to venue selection
      if (result.role === 'SUPERADMIN' && !result.venueId) {
        navigate('/superadmin');
      } else {
        navigate('/bookings');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      await api.requestAdminPasswordReset(forgotEmail);
      setForgotSent(true);
    } catch (err: any) {
      setForgotError(err.message || 'Something went wrong');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-mark">
            <img src={pickleCourtsLogo} alt="PickleCourts logo" />
          </div>
          <h1>PickleCourts</h1>
          <p>Venue Management Portal</p>
        </div>

        {showForgotPassword ? (
          forgotSent ? (
            <div className="auth-form">
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <p style={{ fontSize: '15px', lineHeight: 1.6, marginBottom: '16px' }}>
                  If an account exists for <strong>{forgotEmail}</strong>, you'll receive a password reset email shortly.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSent(false);
                    setForgotEmail('');
                  }}
                  style={{ width: '100%' }}
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleForgotPassword}>
              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.5, marginBottom: '16px' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
              {forgotError && <div className="error-message">{forgotError}</div>}

              <div className="form-group">
                <label htmlFor="forgot-email">Email Address</label>
                <input
                  type="email"
                  id="forgot-email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@business.com"
                  required
                  autoFocus
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <div className="auth-toggle">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowForgotPassword(false);
                    setForgotError('');
                  }}
                >
                  Back to Sign In
                </a>
              </div>
            </form>
          )
        ) : (
          <>
            <form className="auth-form" onSubmit={handleSubmit}>
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                  required
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <label htmlFor="password">Password</label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setForgotEmail(email);
                      setError('');
                    }}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '13px', padding: 0, fontWeight: 500 }}
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-toggle">
              Don't have an account? <Link to="/signup">Create one</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
